import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui';
import type { MerchantAsset } from '@/types/merchant-asset';

interface AssetDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: MerchantAsset | null;
  onConfirm: () => void;
  isPending?: boolean;
}

/**
 * Soft-delete confirmation (Spec 514 §7). The reference warning is advisory
 * (derived `usedBy`) — deletion is never API-blocked; referencing services fall
 * back to their inline URL (no hard fail).
 */
export function AssetDeleteDialog({ open, onOpenChange, asset, onConfirm, isPending }: AssetDeleteDialogProps) {
  const { t } = useTranslation('merchant-assets');

  if (!asset) return null;

  const count = asset.usedBy.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <DialogTitle>{t('remove.title')}</DialogTitle>
              <DialogDescription>{t('remove.warning')}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4 text-sm">
          <p className="font-medium">{asset.name}</p>
          {asset.usageUnavailable ? (
            <p className="mt-2 text-amber-600">{t('remove.unknownUsage')}</p>
          ) : count > 0 ? (
            <p className="mt-2 text-muted-foreground">{t('remove.referenced', { count })}</p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('cancel')}</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending}>
            {t('remove.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
